import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import HealBidBrand from "../HealBidBrand/HealBidBrand";
import { PlatformSiteBannersInline } from "../PlatformMarqueeBanner/PlatformMarqueeBanner";
import { tokenManager } from "../../Api/tokenManager";
import {
  canPatientShellHistoryBack,
  getPatientAppBarBackBehavior,
} from "../PatientAppLayout/patientShellScreenTitle";
import "./PatientAppBar.scss";

const NARROW_APP_BAR_MQ = "(max-width: 1023px)";

function useNarrowAppBar() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(NARROW_APP_BAR_MQ).matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia(NARROW_APP_BAR_MQ);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

function PatientAppBarHistoryBack({ fallbackTo, backLabel, nativeShell }) {
  const navigate = useNavigate();
  const onClick = useCallback(() => {
    if (canPatientShellHistoryBack()) navigate(-1);
    else navigate(fallbackTo);
  }, [navigate, fallbackTo]);

  return (
    <button type="button" className="patient-app-bar__back" onClick={onClick} aria-label={backLabel}>
      <ChevronLeft className="patient-app-bar__back-icon" size={22} strokeWidth={2.25} aria-hidden />
      {!nativeShell ? <span className="patient-app-bar__back-text">{backLabel}</span> : null}
    </button>
  );
}

/**
 * Patient shell top bar — back link or history when stacked; hub tabs show HealBid brand (compact on narrow viewports).
 */
export default function PatientAppBar({
  title,
  subtitle,
  screenTitle,
  showBrand = true,
  backTo,
  backLabel = "Back",
  brandTo,
  /** Reserved for layout padding sync; shell no longer auto-hides the bar on scroll. */
  scrollCollapsed = false,
  /** Shown on the right of the centered `screenTitle` when the native narrow title bar is active (mobile). */
  screenTitleEnd = null,
}) {
  const { pathname } = useLocation();
  const patientSignedIn = Boolean(tokenManager.getTokenForRole("patient"));
  const brandHref = brandTo != null ? brandTo : patientSignedIn ? "/HealBidDashboard" : "/";

  const narrowAppBar = useNarrowAppBar();
  /** Title row only exists on narrow viewports; on desktop it was always hidden via CSS and still stole flex space from the ticker. */
  const withScreenTitle = Boolean(screenTitle && narrowAppBar);
  const nativeShell = withScreenTitle;

  const behavior =
    typeof backTo === "string" ? { kind: "path", to: backTo } : getPatientAppBarBackBehavior(pathname);

  let startLink = null;
  let startHistory = null;
  let startBrand = false;

  if (behavior.kind === "path") {
    startLink = behavior.to;
  } else if (behavior.kind === "history") {
    startHistory = behavior.fallbackTo;
  } else if (showBrand && behavior.kind === "none") {
    startBrand = true;
  }

  return (
    <header
      className={`patient-app-bar${withScreenTitle ? " patient-app-bar--hasScreenTitle" : ""}${
        scrollCollapsed ? " patient-app-bar--scrollCollapsed" : ""
      }`.trim()}
      aria-hidden={scrollCollapsed ? true : undefined}
    >
      <div className="patient-app-bar__safe" aria-hidden />
      <div className="patient-app-bar__inner">
        <div
          className={`patient-app-bar__toolbar${withScreenTitle ? " patient-app-bar__toolbar--withScreenTitle" : ""}`.trim()}
        >
          <div className="patient-app-bar__toolbarStart">
            {startLink ? (
              <Link to={startLink} className="patient-app-bar__back" aria-label={backLabel}>
                <ChevronLeft className="patient-app-bar__back-icon" size={22} strokeWidth={2.25} aria-hidden />
                {!nativeShell ? <span className="patient-app-bar__back-text">{backLabel}</span> : null}
              </Link>
            ) : startHistory ? (
              <PatientAppBarHistoryBack fallbackTo={startHistory} backLabel={backLabel} nativeShell={nativeShell} />
            ) : startBrand ? (
              <HealBidBrand to={brandHref} markOnly={narrowAppBar} />
            ) : (
              <span className="patient-app-bar__edgeSpacer" aria-hidden />
            )}
          </div>
          {!nativeShell ? (
            <div className="patient-app-bar__siteBanners">
              <PlatformSiteBannersInline />
            </div>
          ) : null}
          {withScreenTitle ? <p className="patient-app-bar__screenTitle">{screenTitle}</p> : null}
          {withScreenTitle ? (
            <div className="patient-app-bar__toolbar-spacer patient-app-bar__toolbar-spacer--whenScreenTitle" aria-hidden />
          ) : (
            <div className="patient-app-bar__toolbar-spacer" aria-hidden />
          )}
          <div className="patient-app-bar__toolbarEnd">
            {nativeShell && screenTitle ? (
              screenTitleEnd ? (
                <div className="patient-app-bar__screenTitleEnd">{screenTitleEnd}</div>
              ) : (
                <span className="patient-app-bar__edgeSpacer" aria-hidden />
              )
            ) : null}
          </div>
        </div>
        {(title || subtitle) && (
          <div className="patient-app-bar__headline">
            {title ? <h1 className="patient-app-bar__title">{title}</h1> : null}
            {subtitle ? <p className="patient-app-bar__sub">{subtitle}</p> : null}
          </div>
        )}
      </div>
    </header>
  );
}
